import { useEffect, useMemo, useRef, useState } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import {
  EffectComposer,
  Bloom,
  ToneMapping,
} from '@react-three/postprocessing';
import { ToneMappingMode } from 'postprocessing';
import * as THREE from 'three';
import gsap from 'gsap';
import { REAL_NAME, SIGNATURE_NUMBER } from '../../core/config';
import { useGameStore } from '../../core/store';
import { SKILL_CORES } from '../../data/skills';
import { REVEAL } from '../../data/reveal';
import { JerseyMesh } from '../tunnel/Jersey';
import { TrophyMesh } from '../characters/TrophyMesh';
import { getMaterial } from '../materials';
import {
  play as playAudio,
  setCrowdIntensity,
  startCrowdBed,
} from '../audio';
import { FullscreenCanvasSync } from '../ui/FullscreenCanvasSync';
import './cinematic.css';

function Holograms({ visible }: { visible: boolean }) {
  const group = useRef<THREE.Group>(null);
  const risen = useRef(false);
  const hovering = useRef(false);
  const mats = useMemo(
    () =>
      SKILL_CORES.map(
        (c) =>
          new THREE.MeshStandardMaterial({
            color: c.color,
            emissive: c.color,
            emissiveIntensity: 1.2,
            transparent: true,
            opacity: 0,
            roughness: 0.2,
            metalness: 0.4,
          }),
      ),
    [],
  );

  useEffect(() => {
    if (!visible || !group.current || risen.current) return;
    risen.current = true;
    hovering.current = false;
    const nodes = [...group.current.children];
    let remaining = nodes.length;
    nodes.forEach((child, i) => {
      const endY = child.position.y;
      child.userData.baseY = endY;
      child.position.y = endY - 1.4;
      const mat = (child as THREE.Mesh).material as THREE.MeshStandardMaterial;
      gsap.to(child.position, {
        y: endY,
        duration: 1.1,
        delay: i * 0.06,
        ease: 'power3.out',
        onComplete: () => {
          remaining -= 1;
          if (remaining <= 0) hovering.current = true;
        },
      });
      if (mat) {
        gsap.to(mat, {
          opacity: 0.78,
          duration: 0.85,
          delay: i * 0.06,
          ease: 'power2.out',
        });
      }
    });
  }, [visible]);

  useFrame(({ clock }) => {
    if (!group.current || !visible) return;
    group.current.children.forEach((child, i) => {
      child.rotation.y = clock.elapsedTime * 0.6 + i;
      if (!hovering.current) return;
      const base = child.userData.baseY as number;
      child.position.y = base + Math.sin(clock.elapsedTime * 1.4 + i) * 0.08;
    });
  });

  if (!visible) return null;

  return (
    <group ref={group}>
      {SKILL_CORES.map((c, i) => {
        const ang = (i / SKILL_CORES.length) * Math.PI * 2;
        const r = 2.4;
        return (
          <mesh
            key={c.id}
            position={[Math.cos(ang) * r, 0.8, Math.sin(ang) * r - 2.2]}
            material={mats[i]}
          >
            <icosahedronGeometry args={[0.22, 0]} />
          </mesh>
        );
      })}
      {REVEAL.trophies.slice(0, 3).map((_, i) => {
        const ang = (i / 3) * Math.PI * 2 + 0.4;
        return (
          <mesh
            key={`t-${i}`}
            position={[
              Math.cos(ang) * 1.6,
              1.2 + i * 0.1,
              Math.sin(ang) * 1.6 - 2.2,
            ]}
          >
            <cylinderGeometry args={[0.12, 0.16, 0.35, 8]} />
            <meshStandardMaterial
              color="#f0c46a"
              emissive="#f0c46a"
              emissiveIntensity={0.9}
              transparent
              opacity={0}
            />
          </mesh>
        );
      })}
    </group>
  );
}

function CinematicWorld({
  showHolo,
  camPush,
}: {
  showHolo: boolean;
  camPush: React.MutableRefObject<number>;
}) {
  const grass = useMemo(() => getMaterial('pitchGrass'), []);
  const quality = useGameStore((s) => s.quality);

  useFrame(({ camera }) => {
    const t = camPush.current;
    camera.position.set(0, 1.55 - t * 0.15, 4.2 - t * 2.8);
    camera.lookAt(0, 1.1, -2.2);
  });

  return (
    <>
      <color attach="background" args={['#050d09']} />
      <fog attach="fog" args={['#050d09', 8, 28]} />
      <ambientLight intensity={0.25} color="#8aa898" />
      <spotLight
        position={[2, 6, 4]}
        intensity={60}
        color="#f0c46a"
        angle={0.5}
        penumbra={0.7}
        castShadow
      />
      <spotLight position={[-3, 5, 1]} intensity={30} color="#d8e8ff" angle={0.45} />
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0, -2]} receiveShadow material={grass}>
        <planeGeometry args={[16, 16]} />
      </mesh>
      <JerseyMesh position={[0, 0.95, -2.2]} />
      <TrophyMesh active position={[1.4, 1.35, -1.6]} scale={0.85} />
      <Holograms visible={showHolo} />
      <EffectComposer multisampling={quality.antialias ? 2 : 0}>
        <Bloom intensity={0.4} luminanceThreshold={0.65} mipmapBlur />
        <ToneMapping mode={ToneMappingMode.ACES_FILMIC} />
      </EffectComposer>
    </>
  );
}

export function CinematicReveal() {
  const finishCinematic = useGameStore((s) => s.finishCinematic);
  const revealIdentity = useGameStore((s) => s.revealIdentity);
  const setOverlayOpacity = useGameStore((s) => s.setOverlayOpacity);
  const profileName = useGameStore((s) => s.profile.name);

  const [line, setLine] = useState('');
  const [showHolo, setShowHolo] = useState(false);
  const [nameTag, setNameTag] = useState(profileName);
  const camPush = useRef(0);
  const root = useRef<HTMLDivElement>(null);

  useEffect(() => {
    startCrowdBed();
    setCrowdIntensity(0);
    playAudio('silence');
    // Keep default lag smoothing — (0) lets a slow WebGL first frame
    // jump the whole timeline and skip the identity beat.

    const timers: number[] = [];
    const tl = gsap.timeline();

    // 1–2: silence + camera push
    tl.to(camPush, { current: 1, duration: 3.2, ease: 'power2.inOut' }, 0);

    // Wall-clock beats so identity reveal still fires if rAF is throttled
    timers.push(
      window.setTimeout(() => setNameTag(''), 1600),
      window.setTimeout(() => {
        revealIdentity();
        setNameTag(REAL_NAME);
        playAudio('goal');
        setCrowdIntensity(0.95);
      }, 2400),
      window.setTimeout(() => {
        setLine(
          "You didn't just visit my portfolio.",
        );
        void import('../audio/voice').then(({ speakLine }) =>
          speakLine(
            "You didn't just visit my portfolio.",
            { rate: 0.88 },
          ),
        );
      }, 3400),
      window.setTimeout(() => {
        setLine(
          'You experienced my journey.',
        );
        void import('../audio/voice').then(({ speakLine }) =>
          speakLine(
            'You experienced my journey.',
            { rate: 0.88 },
          ),
        );
      }, 5600),
      window.setTimeout(() => setShowHolo(true), 6400),
      window.setTimeout(() => {
        setOverlayOpacity(1);
        timers.push(
          window.setTimeout(() => {
            finishCinematic();
            setOverlayOpacity(0);
          }, 500),
        );
      }, 10000),
    );

    // Mirror same beats on the timeline for smooth foreground play
    tl.add(() => setNameTag(''), 1.6);
    tl.add(() => {
      revealIdentity();
      setNameTag(REAL_NAME);
      playAudio('goal');
      setCrowdIntensity(0.95);
    }, 2.4);
    tl.add(() => {
      setLine(
        "You didn't just visit my portfolio.",
      );
    }, 3.4);
    tl.add(() => {
      setLine(
        'You experienced my journey.',
      );
    }, 5.6);
    tl.add(() => setShowHolo(true), 6.4);

    // Absolute failsafe → reveal
    timers.push(
      window.setTimeout(() => {
        finishCinematic();
        setOverlayOpacity(0);
      }, 14000),
    );

    return () => {
      tl.kill();
      timers.forEach((t) => window.clearTimeout(t));
    };
  }, [finishCinematic, revealIdentity, setOverlayOpacity]);

  return (
    <div className="cinematic" ref={root}>
      <div className="cinematic__canvas">
        <Canvas
          dpr={[1, 1.5]}
          frameloop="always"
          style={{ width: '100%', height: '100%', display: 'block' }}
          camera={{ position: [0, 1.55, 4.2], fov: 45, near: 0.1, far: 60 }}
          shadows
          onCreated={({ gl }) => gl.setClearColor('#050d09')}
        >
          <FullscreenCanvasSync />
          <CinematicWorld showHolo={showHolo} camPush={camPush} />
        </Canvas>
      </div>

      <div className="cinematic__nameplate">
        <p className="cinematic__eyebrow">ADARSH DHAWALE</p>
        <h2>{nameTag || '· · ·'}</h2>
        {nameTag === REAL_NAME && (
          <p className="cinematic__number">#{SIGNATURE_NUMBER}</p>
        )}
      </div>

      {line && <p className="cinematic__line">{line}</p>}
    </div>
  );
}
